/**
 * Interview / transcript helpers shared by Dashboard runtime and Studio.
 */

/** Panel binding source paths that resolve from app interview state (not flow output). */
export const APP_BINDING_SOURCE_HINTS = [
  'app.chatHistory',
  'app.interviewTranscript',
  'app.interviewSession',
  'app.interviewSession.phases',
  'app.interviewSession.currentPhaseId',
  'app.interviewSession.currentPhase',
];

/**
 * @param {unknown} raw
 * @returns {{ id: string, label: string, order: number }[]}
 */
export function normalizeInterviewPhases(raw) {
  let arr = raw;
  if (typeof raw === 'string') {
    try {
      arr = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(arr)) return [];
  return arr.map((p, i) => {
    const id = String(p?.id ?? p?.key ?? `phase_${i}`).trim() || `phase_${i}`;
    const label = String(p?.label ?? p?.name ?? id).trim() || id;
    const order = Number.isFinite(Number(p?.order)) ? Number(p.order) : i;
    return { id, label, order };
  });
}

/**
 * @param {unknown} arr
 * @param {number} maxItems
 * @returns {Array<{ role: string, content: string, phaseId?: string, at?: string, seq: number }>}
 */
export function sanitizeTranscriptTurns(arr, maxItems) {
  if (!Array.isArray(arr)) return [];
  const cap = Math.min(300, Math.max(0, Number(maxItems) || 120));
  return arr.slice(-cap).map((t, i) => {
    const role = String(t?.role || '').toLowerCase();
    const content = typeof t?.content === 'string' ? t.content : (t?.content != null ? String(t.content) : '');
    const phaseId = t?.phaseId != null ? String(t.phaseId) : undefined;
    const at = typeof t?.at === 'string' ? t.at : (t?.ts != null ? new Date(Number(t.ts)).toISOString() : undefined);
    const seq = Number.isFinite(Number(t?.seq)) ? Number(t.seq) : i;
    return { role, content, phaseId, at, seq };
  }).filter((t) => t.content && (t.role === 'user' || t.role === 'assistant'));
}

/**
 * @param {{
 *   chatHistory: unknown,
 *   interviewTranscript: unknown,
 *   interviewPhaseId: string | null | undefined,
 *   phases: unknown,
 * }} p
 */
/** Resolve persisted flow id for a panel (V2 apps use source.flowRef). */
export function resolvePanelFlowRef(panel) {
  const ref = panel?.source?.flowRef || panel?.flowId || panel?.flow_id;
  return ref ? String(ref) : null;
}

export function buildInterviewAppBindingSnapshot(p) {
  const phases = normalizeInterviewPhases(p?.phases);
  const pid = p?.interviewPhaseId != null && String(p.interviewPhaseId).trim()
    ? String(p.interviewPhaseId).trim()
    : null;
  const currentPhase = pid ? phases.find((x) => x.id === pid) || null : null;
  return {
    chatHistory: Array.isArray(p?.chatHistory) ? p.chatHistory : [],
    interviewTranscript: Array.isArray(p?.interviewTranscript) ? p.interviewTranscript : [],
    interviewSession: {
      phases,
      currentPhaseId: pid,
      currentPhase,
    },
  };
}

/**
 * Resolve `app.*` binding paths against {@link buildInterviewAppBindingSnapshot}.
 * @param {string} sourcePath
 * @param {ReturnType<typeof buildInterviewAppBindingSnapshot>} snapshot
 * @returns {{ isApp: false } | { isApp: true, value: unknown }}
 */
export function resolveAppBindingSource(sourcePath, snapshot) {
  const s = String(sourcePath || '').trim();
  if (!s.startsWith('app.')) return { isApp: false };
  const parts = s.slice(4).split('.').filter(Boolean);
  let cur = snapshot;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') {
      return { isApp: true, value: undefined };
    }
    cur = cur[part];
  }
  return { isApp: true, value: cur };
}
